import React, { ReactNode, useCallback } from 'react';
import styled, { css } from 'styled-components';

import { Option as BlankOptionTile } from 'src/components/option-tile/option.styles';

import { useCardContext } from '../card-context';
import { OptionProvider } from './option-context';

const DEFAULT_MAX_VISIBLE = 8;

type OptionTileListProps = {
  /** Content rendered inside each tile, wrapped in an OptionProvider per option. */
  children: ReactNode;
  /** Maximum tiles before showing an overflow "+N more" tile. Defaults to 8. */
  maxVisible?: number;
  /** Horizontal alignment of the tile list. Defaults to 'end'. */
  alignment?: 'center' | 'end' | 'start';
  /** Gap between tiles (px). Defaults to 8. */
  gap?: number;
};

/**
 * Repeater component for option tiles in the list card.
 * Iterates over options from CardContext and renders `children` once per option,
 * each wrapped in an OptionProvider that supplies that option's data to tile primitives.
 */
export function OptionTileList({
  children,
  maxVisible = DEFAULT_MAX_VISIBLE,
  alignment = 'end',
  gap = 8,
}: OptionTileListProps): JSX.Element | null {
  const { options, listPrice, isComingSoon, onOptionClick } = useCardContext();

  const handleOverflowClick = useCallback(() => {
    onOptionClick?.('');
  }, [onOptionClick]);

  if (isComingSoon || !options || !listPrice) {
    return null;
  }

  const { items, totalCount, hasMultiple, shouldDisplayLabel } = options;
  const numberToDisplay = totalCount > maxVisible ? maxVisible - 1 : totalCount;
  const isNarrowLayout = numberToDisplay === 5 || numberToDisplay === 6;

  return (
    <OuterWrapper>
      <TileGrid $alignment={alignment} $gap={gap} $isNarrowLayout={isNarrowLayout}>
        {items.slice(0, numberToDisplay).map((option) => (
          <TileShell
            key={option.value}
            type='button'
            $hasMultipleOptions={hasMultiple}
            onClick={() => onOptionClick?.(option.value)}
            data-testid='option-tile'
          >
            <OptionProvider
              option={option}
              listPrice={listPrice}
              hasMultipleOptions={hasMultiple}
              shouldDisplayLabel={shouldDisplayLabel}
            >
              {children}
            </OptionProvider>
          </TileShell>
        ))}

        {totalCount > maxVisible && (
          <BlankOptionTile onClick={handleOverflowClick} data-testid='blank-option-tile' $blank $hasMultipleOptions>
            +{totalCount - numberToDisplay} more <br />
            options
          </BlankOptionTile>
        )}
      </TileGrid>
    </OuterWrapper>
  );
}

export const OuterWrapper = styled.div`
  @media screen and (max-width: 739px) {
    margin: 0 -16px;
    overflow-x: auto;
    padding: 0 16px 0 ${16 + 92 + 20}px;
    -ms-overflow-style: none;
    scroll-behavior: smooth;
    scrollbar-width: none;

    ::-webkit-scrollbar {
      display: none;
    }
  }
`;

export const alignmentMap = { start: 'flex-start', center: 'center', end: 'flex-end' } as const;

export const TileGrid = styled.div<{ $alignment: 'center' | 'end' | 'start'; $gap: number; $isNarrowLayout?: boolean }>`
  align-items: center;
  display: flex;
  gap: ${({ $gap }) => $gap}px;

  @media screen and (max-width: 739px) {
    margin-top: 20px;
  }

  @media screen and (min-width: 740px) {
    flex-wrap: wrap;
    justify-content: ${({ $alignment }) => alignmentMap[$alignment]};
    min-width: 312px;
    max-width: 312px;

    ${({ $isNarrowLayout }) =>
      $isNarrowLayout &&
      css`
        min-width: 240px;
        max-width: 240px;
      `}
  }
`;

export const TileShell = styled.button<{ $hasMultipleOptions?: boolean }>`
  align-items: center;
  appearance: none;
  background: var(--dcm-product-option-bg, #f5f6f7);
  border: none;
  border-radius: ${({ theme }) => theme.customized.radius.sm};
  color: var(--dcm-product-option-text-color, #121516);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  flex: 0 0 96px;
  font-size: 12px;
  justify-content: center;
  line-height: ${13 / 12};
  min-height: 60px;
  padding: 2px;
  position: relative;
  transition: all 0.3s;

  @media screen and (min-width: 740px) {
    min-height: 68px;
    flex-basis: 72px;
  }

  ${({ $hasMultipleOptions }) =>
    !$hasMultipleOptions &&
    css`
      flex-basis: 100%;

      @media screen and (min-width: 740px) {
        flex-basis: 152px;
      }
    `}

  &:hover {
    background: var(--dcm-color-surface-hover, #e3e7e9);
  }
`;
